import type { Warning } from '@/components/common/WarningIcon'

import { formatPreciseDuration } from '@/components/VaultPage/helpers/timelock'

import { getTimelockBelowMinimumWarning, type VaultV2TimelocksWarning } from '../utils'

export const CAP_CHANGE_NOTICE_TARGET_SECONDS = 7 * 24 * 60 * 60

export const CAP_CHANGE_NOTICE_BELOW_TARGET_NOTE = 'Recommended: 7 days'

export const CAP_CHANGE_NOTICE_MEETS_TARGET_TOOLTIP = 'Meets the recommended timelock duration'

export type CapChangeNoticeFunctionName =
  | 'addAdapter'
  | 'increaseAbsoluteCap'
  | 'increaseRelativeCap'

export type CapChangeNoticeRowStatus =
  | 'abdicated'
  | 'meetsTarget'
  | 'belowTarget'
  | 'belowMinimum'
  | 'critical'

export interface CapChangeNoticeTimelockEntry {
  functionName: string
  durationSeconds: number
  abdicatedAt: number | null
}

export interface CapChangeNoticeRow {
  functionName: CapChangeNoticeFunctionName
  label: string
  description: string
  durationSeconds: number | null
  formattedDuration: string
  status: CapChangeNoticeRowStatus
  statusNote?: string
  iconTooltip?: string
  warning?: Warning
}

export interface CapChangeNoticeState {
  rows: [CapChangeNoticeRow, CapChangeNoticeRow, CapChangeNoticeRow]
}

export const CAP_CHANGE_NOTICE_FUNCTIONS: Record<
  'adapter' | 'absolute' | 'relative',
  CapChangeNoticeFunctionName
> = {
  adapter: 'addAdapter',
  absolute: 'increaseAbsoluteCap',
  relative: 'increaseRelativeCap'
}

export const CAP_CHANGE_NOTICE_ROW_COPY: Record<
  CapChangeNoticeFunctionName,
  { label: string; description: string }
> = {
  addAdapter: {
    label: 'Add adapter',
    description: 'Time before a curator can enable a new adapter to the vault'
  },
  increaseAbsoluteCap: {
    label: 'Increase absolute cap',
    description: 'Time before a curator can raise the maximum amount supplied to an allocation'
  },
  increaseRelativeCap: {
    label: 'Increase relative cap',
    description:
      'Time before a curator can raise the maximum share of the vault supplied to an allocation'
  }
}

const getRow = (
  entries: CapChangeNoticeTimelockEntry[],
  warning: VaultV2TimelocksWarning,
  functionName: CapChangeNoticeFunctionName
): CapChangeNoticeRow => {
  const copy = CAP_CHANGE_NOTICE_ROW_COPY[functionName]
  const entry = entries.find(timelock => timelock.functionName === functionName)

  if (!entry) {
    return {
      functionName,
      label: copy.label,
      description: copy.description,
      durationSeconds: null,
      formattedDuration: 'Unavailable',
      status: 'critical'
    }
  }

  const { durationSeconds, abdicatedAt } = entry
  if (abdicatedAt != null) {
    return {
      functionName,
      label: copy.label,
      description: copy.description,
      durationSeconds,
      formattedDuration: 'Abdicated',
      status: 'abdicated'
    }
  }

  const isCritical = !Number.isFinite(durationSeconds) || durationSeconds <= 0

  if (isCritical) {
    return {
      functionName,
      label: copy.label,
      description: copy.description,
      durationSeconds,
      formattedDuration: 'No timelock',
      status: 'critical'
    }
  }

  // A backend failed check means the timelock is below the protocol-required minimum for this
  // function (a stricter, per-function floor than the 7-day recommendation). It renders red with
  // the same "below the N days minimum" warning as the other timelock rows.
  const belowMinimumWarning = getTimelockBelowMinimumWarning(warning, functionName)
  if (belowMinimumWarning) {
    return {
      functionName,
      label: copy.label,
      description: copy.description,
      durationSeconds,
      formattedDuration: formatPreciseDuration(durationSeconds),
      status: 'belowMinimum',
      warning: belowMinimumWarning
    }
  }

  const meetsTarget = durationSeconds >= CAP_CHANGE_NOTICE_TARGET_SECONDS
  if (meetsTarget) {
    return {
      functionName,
      label: copy.label,
      description: copy.description,
      durationSeconds,
      formattedDuration: formatPreciseDuration(durationSeconds),
      status: 'meetsTarget',
      iconTooltip: CAP_CHANGE_NOTICE_MEETS_TARGET_TOOLTIP
    }
  }

  return {
    functionName,
    label: copy.label,
    description: copy.description,
    durationSeconds,
    formattedDuration: formatPreciseDuration(durationSeconds),
    status: 'belowTarget',
    statusNote: CAP_CHANGE_NOTICE_BELOW_TARGET_NOTE
  }
}

export const getCapChangeNoticeState = (
  entries: CapChangeNoticeTimelockEntry[],
  warning: VaultV2TimelocksWarning
): CapChangeNoticeState => {
  return {
    rows: [
      getRow(entries, warning, CAP_CHANGE_NOTICE_FUNCTIONS.adapter),
      getRow(entries, warning, CAP_CHANGE_NOTICE_FUNCTIONS.absolute),
      getRow(entries, warning, CAP_CHANGE_NOTICE_FUNCTIONS.relative)
    ]
  }
}
